import * as React from 'react';
import _ from 'lodash';
import cn from '~modules/cn';

import './button-group.scss';

export type ButtonItem<T> = {
    title: string;
    children?: JSX.Element;
    value: T;
    disabled?: boolean;
};

export type ButtonGroupSize = 'small' | 'medium';

type BaseProps<T, V = T> = {
    type?: 'inline' | 'inline-block';
    className?: string;
    size?: ButtonGroupSize;
    items: ButtonItem<T>[];
    value?: V;
    onChange?: (value: V) => void;
    disabled?: boolean;
    ref?: React.Ref<HTMLDivElement> | null;
};

type Props<T> = ({ multiple: true } & BaseProps<T, T[]>) | ({ multiple?: false } & BaseProps<T>);

export default function ButtonGroup<T extends string | number | boolean>(props: Props<T>): JSX.Element {
    const { size = 'medium' } = props;
    const value = props.multiple ? _.castArray(props.value) : props.value;
    return (
        <div
            ref={props.ref}
            className={cn('button-group', props.className, {
                'button-group--disabled': props.disabled,
                [`button-group--type-${props.type}`]: props.type,
                [`button-group--size-${size}`]: size
            })}
        >
            {props.items.map((item) => (
                <button
                    key={item.value?.toString()}
                    type="button"
                    className={cn('button-group__button', {
                        'button-group__button--selected': Array.isArray(value) ? value?.includes(item.value) : item.value === props.value
                    })}
                    disabled={item.disabled}
                    onClick={() => {
                        let newValue: T | T[];
                        if (props.multiple) {
                            newValue = _.castArray(props.value).slice();
                            const itemIndex = newValue.indexOf(item.value);
                            if (itemIndex >= 0) {
                                newValue.splice(itemIndex, 1);
                            } else {
                                newValue.push(item.value);
                            }
                        } else {
                            newValue = item.value;
                        }
                        if (!_.isEqual(props.value, newValue)) {
                            props.onChange?.(newValue as $TSFixMe);
                        }
                    }}
                >
                    {item.children || item.title}
                </button>
            ))}
        </div>
    );
}
